import { QueryBuilder } from 'vet-bones/utils/queryBuilder';

import { DataSourceType } from 'src/constants/dataSourceTypeDescription';
import { ProjectResourceType } from 'src/constants/project';
import {
  HiddenCategory,
  LinkCategory,
  LinkLabelCategory,
  NodeCategory,
} from 'src/containers/ProjectDiagram/constants/categories';

// base data

export interface BaseData {
  key: string;
  value: string;
  category: NodeCategory | LinkLabelCategory | LinkCategory | HiddenCategory;

  hidden?: boolean;
}

export interface BaseNodeData extends BaseData {
  // key is `${category}-${value}`
  category: NodeCategory;

  pos?: string;
}

export interface BaseLinkLabelData extends BaseData {
  // key is `${category}-${from}-${value}-${to}`
  category: LinkLabelCategory;

  from?: string;
  to?: string;
}

export interface BaseLinkData extends BaseData {
  // key is `${category}-${from}-${value}-${to}`
  category: LinkCategory;

  from?: string;
  to?: string;
  labelKeys: string[];

  points?: number[] | go.List<go.Point>;
}

export interface HiddenData extends Omit<BaseData, 'value'> {
  value: string | null;
  category: HiddenCategory;
}

// resources

export type ResourceField = {
  label: string;
  value: string;
};

export interface Resource extends BaseNodeData {
  category: NodeCategory.RESOURCE;
  type: ProjectResourceType;

  fields: ResourceField[];
  text: string;
}

export interface FileUpload extends Resource {
  type: ProjectResourceType.FILE_UPLOAD;
}

export interface VirtualGraph extends Resource {
  type: ProjectResourceType.VIRTUAL_GRAPH;
  dataSourceType: DataSourceType;
}

// deleted resources

export interface DeletedResource extends BaseNodeData {
  category: NodeCategory.DELETED_RESOURCE;
  type: ProjectResourceType;

  text: string;
}

export interface DeletedFileUpload extends Resource {
  type: ProjectResourceType.FILE_UPLOAD;
}

export interface DeletedVirtualGraph extends Resource {
  type: ProjectResourceType.VIRTUAL_GRAPH;
  dataSourceType: DataSourceType;
}

// mappings

export interface MappingLabel extends BaseLinkLabelData {
  category: LinkLabelCategory.MAPPING_LABEL;
  from: string;

  text: string;
}

export type MappingField = {
  label: string;
  value: string;
  targets: string[];
};

export enum MappingIdentifierType {
  DELIMITER = 'delimiter',
  FIELD = 'field',
}

export type MappingIdentifierPart = {
  type: MappingIdentifierType;
  value: string;
};

export type MappingIdentifier = {
  baseIri: string | null;
  parts: MappingIdentifierPart[];
};

export interface Mapping extends BaseLinkData {
  category: LinkCategory.MAPPING;
  from: string;

  text: string;
  primaryIdentifier: MappingIdentifier;
  label: string | null;
  fields: Record<string, MappingField>;
  setClassType: boolean;
  uniqueKeySets: string[];
}

export type RelationshipMapping =
  | (BaseLinkData & {
      // while conceptually a link, a relationship mapping
      // will not have labels or positions
      category: LinkCategory.RELATIONSHIP_MAPPING;
      relationshipIri: string;

      from?: never;
      fromClassIri?: string;
      fromPrimaryIdentifier?: MappingIdentifier;

      to: string;
      toClassIri?: never;
      toPrimaryIdentifier?: never;
    })
  | (BaseLinkData & {
      // while conceptually a link, a relationship mapping
      // will not have labels or positions
      category: LinkCategory.RELATIONSHIP_MAPPING;
      relationshipIri: string;

      from: string;
      fromClassIri?: never;
      fromPrimaryIdentifier?: never;

      to?: never;
      toClassIri?: string;
      toPrimaryIdentifier?: MappingIdentifier;
    });

export const enum MappingType {
  CLASS = 'class',
  RELATIONSHIP = 'relationship',
  UNCONNECTED = 'unconnected',
  NONE = 'none',
}

// deleted mappings

export interface DeletedMappingLabel extends BaseLinkLabelData {
  category: LinkLabelCategory.DELETED_MAPPING_LABEL;
  from: string;

  text: string;
}

export interface DeletedMapping extends BaseLinkData {
  category: LinkCategory.DELETED_MAPPING;
  from: string;

  text: string;
}

export type DeletedRelationshipMapping =
  | (BaseLinkData & {
      // while conceptually a link, a relationship mapping
      // will not have labels or positions
      category: LinkCategory.DELETED_RELATIONSHIP_MAPPING;
      relationshipIri: string;

      from?: never;
      fromClassIri?: string;

      to: string;
      toClassIri?: never;

      text: string;
    })
  | (BaseLinkData & {
      // while conceptually a link, a relationship mapping
      // will not have labels or positions
      category: LinkCategory.DELETED_RELATIONSHIP_MAPPING;
      relationshipIri: string;

      from: string;
      fromClassIri?: never;

      to?: never;
      toClassIri?: string;

      text: string;
    });

// model concepts

export interface ModelConceptAttributeData {
  value: string;
}

export interface ModelConceptAttribute {
  value: string;
  domains: Record<string, ModelConceptAttributeData>;
  ranges: Record<string, ModelConceptAttributeData>;
}

export interface ModelConceptAttributeWithHierarchyData
  extends ModelConceptAttribute {
  depth: number; // for now, mainly used to determine if inherited/not inherited
  sourceClassIris: Set<string>; // classes from same depth of ancestry
}

export const enum ModelConceptClassHierarchy {
  CHILDREN = 'children',
  PARENTS = 'parents',
}

export interface ModelConceptClass extends BaseNodeData {
  category: NodeCategory.MODEL_CONCEPT_CLASS;

  isAttributesOpen: boolean;
}

export interface ModelConceptRelationshipLabel extends BaseLinkLabelData {
  category: LinkLabelCategory.MODEL_CONCEPT_RELATIONSHIP_LABEL;
}

export interface ModelConceptRelationship extends BaseLinkData {
  category: LinkCategory.MODEL_CONCEPT_RELATIONSHIP;
}

export interface ModelConceptRelationshipWithHierarchyData
  extends ModelConceptRelationship {
  depth: number; // for now, mainly used to determine if inherited/not inherited
  sourceClassIris: Set<string>; // classes from same depth of ancestry
}

export interface ModelConceptHierarchyLabel extends BaseLinkLabelData {
  category: LinkLabelCategory.MODEL_CONCEPT_HIERARCHY_LABEL;
  from: string;
  to: string;
}
export interface ModelConceptHierarchy extends BaseLinkData {
  category: LinkCategory.MODEL_CONCEPT_HIERARCHY;
  from: string;
  to: string;
}

export interface ModelConceptHiddenAttribute extends HiddenData {
  category: HiddenCategory.MODEL_CONCEPT_ATTRIBUTE;
}

// deleted model concepts

export interface DeletedModelConceptClass extends BaseNodeData {
  category: NodeCategory.DELETED_MODEL_CONCEPT_CLASS;

  text: string;
}

export interface DeletedModelConceptRelationshipLabel
  extends BaseLinkLabelData {
  category: LinkLabelCategory.DELETED_MODEL_CONCEPT_RELATIONSHIP_LABEL;

  text: string;
}

export interface DeletedModelConceptRelationship extends BaseLinkData {
  category: LinkCategory.DELETED_MODEL_CONCEPT_RELATIONSHIP;

  text: string;
}

export interface DeletedModelConceptHierarchyLabel extends BaseLinkLabelData {
  category: LinkLabelCategory.DELETED_MODEL_CONCEPT_HIERARCHY_LABEL;
  from: string;
  to: string;
}
export interface DeletedModelConceptHierarchy extends BaseLinkData {
  category: LinkCategory.DELETED_MODEL_CONCEPT_HIERARCHY;
  from: string;
  to: string;
}

// other model data

export enum ModelRuleType {
  TEXT = 'text',
  QUERY_BUILDER = 'query_builder',
}

export type ModelRule = {
  id: string;
  models: string[];
  name: string;
  ifClause: QueryBuilder | null;
  thenClause: QueryBuilder | null;
  text: string;
  type: ModelRuleType;
};
